import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { safeRest } from '@wds/utils';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    palette = theme.palette,
    type = theme.type,
    motion = theme.motion;
  var width = function width(s) {
    return {
      xs: spacing[4],
      sm: spacing[6],
      md: spacing[8],
      lg: spacing[10]
    }[s];
  };
  var font = function font(s) {
    return {
      xs: type.fontSize.bodySm,
      sm: type.fontSize.bodySm,
      md: type.fontSize.body,
      lg: type.fontSize.pageTitle
    }[s];
  };
  var fontWeight = function fontWeight(s) {
    return {
      xs: type.fontWeight.regular,
      sm: type.fontWeight.regular,
      md: type.fontWeight.medium,
      lg: type.fontWeight.medium
    }[s];
  };
  var height = width;
  var rotate = {
    '@keyframes rotate': {
      '100%': {
        transform: 'rotate(360deg)'
      }
    }
  };
  var stretch = {
    '@keyframes stretch': {
      '0%': {
        strokeDasharray: '339.292, 339.292',
        strokeDashoffset: '203.57519999999997',
        animationTimingFunction: 'cubic-bezier(0.3, 0, 1, 1)'
      },
      '40%': {
        strokeDasharray: '339.292, 339.292',
        strokeDashoffset: '253.57519999999997',
        animationTimingFunction: 'cubic-bezier(0, 0, 0.9, 1)'
      },
      '65%': {
        strokeDashoffset: '303.57519999999997'
      },
      '100%': {
        strokeDashoffset: '203.57519999999997'
      }
    }
  };
  var fillColor = function fillColor(color) {
    return color === 'dark' || color === 'light' ? palette.text[color] : palette[color].main;
  };
  var bgColor = function bgColor(color) {
    if (color === 'dark') {
      return palette.transparent.dark;
    }
    return color === 'light' ? palette.transparent.light : palette.muted.main;
  };
  var progressCircleStyles = __assign(__assign(__assign({}, rotate), stretch), {
    wrenchProgressCircle: {
      display: 'inline-block',
      position: 'relative',
      width: function (_a) {
        var size = _a.size;
        return width(size);
      },
      height: function (_a) {
        var size = _a.size;
        return height(size);
      },
      fill: 'none',
      '-webkit-transform': function (_a) {
        var continuous = _a.continuous;
        return continuous && 'translate3d(0,0,0)';
      }
    },
    wrenchCirclePath: {
      transform: 'rotate(-90deg)'
    },
    wrenchCirclePathGroup: {
      animation: function (_a) {
        var continuous = _a.continuous;
        return continuous && '$rotate 1s linear infinite';
      },
      transformOrigin: function (_a) {
        var continuous = _a.continuous;
        return continuous && 'center center';
      }
    },
    wrenchCirclePathBorder: {
      stroke: function (_a) {
        var color = _a.color;
        return bgColor(color);
      }
    },
    wrenchCirclePathFill: {
      transition: function (_a) {
        var animateOnLoad = _a.animateOnLoad;
        return animateOnLoad && "all 1s ".concat(motion.curve.ease);
      },
      stroke: function (_a) {
        var color = _a.color;
        return fillColor(color);
      },
      strokeLinecap: 'round',
      strokeDasharray: function (_a) {
        var continuous = _a.continuous;
        return continuous && '339.292, 339.292';
      },
      strokeDashoffset: function (_a) {
        var continuous = _a.continuous;
        return continuous && '203.57519999999997';
      },
      animation: function (_a) {
        var continuous = _a.continuous;
        return continuous && '$stretch 1s infinite';
      },
      transformOrigin: function (_a) {
        var continuous = _a.continuous;
        return continuous && 'center center';
      }
    },
    wrenchCircleDisplay: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: function (_a) {
        var size = _a.size;
        return width(size);
      },
      height: function (_a) {
        var size = _a.size;
        return height(size);
      },
      fontSize: function (_a) {
        var size = _a.size;
        return font(size);
      },
      fontWeight: function (_a) {
        var size = _a.size;
        return fontWeight(size);
      },
      display: 'inline-flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center'
    }
  });
  return progressCircleStyles;
});

var ProgressCircle = function ProgressCircle(_a) {
  var children = _a.children,
    _b = _a.size,
    size = _b === void 0 ? 'md' : _b,
    percent = _a.percent,
    _c = _a.color,
    color = _c === void 0 ? 'success' : _c,
    _d = _a.continuous,
    continuous = _d === void 0 ? false : _d,
    valueMax = _a.valueMax,
    valueMin = _a.valueMin,
    valueText = _a.valueText,
    _e = _a.animateOnLoad,
    animateOnLoad = _e === void 0 ? false : _e,
    rest = __rest(_a, ["children", "size", "percent", "color", "continuous", "valueMax", "valueMin", "valueText", "animateOnLoad"]);
  var initialPercent = animateOnLoad ? 0 : percent;
  var _f = useState(initialPercent),
    p = _f[0],
    setPercent = _f[1];
  useEffect(function () {
    return setPercent(percent);
  }, [percent]);
  var _g = useStyles({
      color: color,
      size: size,
      continuous: continuous,
      animateOnLoad: animateOnLoad
    }),
    wrenchProgressCircle = _g.wrenchProgressCircle,
    wrenchCirclePath = _g.wrenchCirclePath,
    wrenchCirclePathGroup = _g.wrenchCirclePathGroup,
    wrenchCirclePathBorder = _g.wrenchCirclePathBorder,
    wrenchCirclePathFill = _g.wrenchCirclePathFill,
    wrenchCircleDisplay = _g.wrenchCircleDisplay;
  var circleOptions = {
    lg: {
      strokeWidth: 8,
      radius: 56
    },
    sm: {
      strokeWidth: 16,
      radius: 52
    },
    md: {
      strokeWidth: 12,
      radius: 54
    },
    xs: {
      strokeWidth: 20,
      radius: 50
    }
  };
  var _h = circleOptions[size],
    strokeWidth = _h.strokeWidth,
    radius = _h.radius;
  var circumference = radius * 2 * Math.PI;
  var valueNow = p != null ? p : undefined;
  var strokeDasharray = !continuous ? "".concat(circumference, " ").concat(circumference) : undefined;
  var strokeDashoffset = !continuous && p != null ? circumference * ((100 - p) / 100) : undefined;
  return jsxs("div", __assign({
    className: wrenchProgressCircle,
    role: "progressbar",
    "aria-valuenow": valueNow,
    "aria-valuemax": valueMax,
    "aria-valuemin": valueMin,
    "aria-valuetext": valueText
  }, safeRest(rest), {
    children: [jsx("svg", {
      className: wrenchCirclePath,
      role: "img",
      "aria-label": "Progress",
      viewBox: "0 0 120 120",
      children: jsxs("g", {
        className: wrenchCirclePathGroup,
        children: [jsx("circle", {
          className: wrenchCirclePathBorder,
          cx: "60",
          cy: "60",
          r: radius,
          strokeWidth: strokeWidth
        }), jsx("circle", {
          className: wrenchCirclePathFill,
          cx: "60",
          cy: "60",
          r: radius,
          strokeWidth: strokeWidth,
          strokeDasharray: strokeDasharray,
          strokeDashoffset: strokeDashoffset
        })]
      })
    }), children && jsx("div", {
      className: wrenchCircleDisplay,
      children: children
    })]
  }));
};

var PROGRESS_CIRCLE_COLORS = ['primary', 'secondary', 'success', 'warning', 'error', 'upgrade', 'info', 'dark', 'light'];
var PROGRESS_CIRCLE_SIZES = ['xs', 'sm', 'md', 'lg'];

export { PROGRESS_CIRCLE_COLORS, PROGRESS_CIRCLE_SIZES, ProgressCircle };
